import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  GoalMilestone,
  ScheduleEvent,
  ScheduleEventLocation,
  ScheduleEventType,
  TrainingGoal,
} from '@shared/types';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfWeek,
  subMonths,
} from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { EventEditorModal } from './EventEditorModal';
import { EVENT_TYPE_CONFIG, EventEditorPopover, LOCATION_CONFIG } from './EventEditorPopover';

/**
 * Generate a unique ID for schedule events
 */
function generateEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export interface ScheduleAgendaProps {
  /** Current schedule events from user profile */
  scheduleEvents: ScheduleEvent[];
  /** Callback when events are updated */
  onEventsChange: (events: ScheduleEvent[]) => void;
  /** User's training goal (for displaying milestones) */
  trainingGoal?: TrainingGoal;
  /** Whether there are pending changes to sync */
  hasPendingChanges?: boolean;
  /** Callback to trigger plan update via agent */
  onUpdateSchedule?: () => void;
  /** Loading state for update action */
  isUpdating?: boolean;
}

/**
 * Generate goal milestones from a training goal
 * Only shows the main race/goal date - no intermediate milestones
 */
function generateGoalMilestones(goal?: TrainingGoal): GoalMilestone[] {
  if (!goal?.targetDate) return [];

  const milestones: GoalMilestone[] = [];
  const raceDate = parseISO(goal.targetDate);
  const today = new Date();

  // Only show milestones for future dates
  if (isBefore(raceDate, today)) return [];

  // Race day only - no intermediate milestones
  milestones.push({
    date: goal.targetDate,
    type: 'race',
    label: goal.goal || 'Goal Race',
    navigateToGoal: true,
  });

  return milestones;
}

/**
 * Custom day component for the calendar
 */
interface CustomDayProps {
  day: Date;
  selectedDay: Date | null;
  outsideCurrentMonth: boolean;
  events: ScheduleEvent[];
  milestones: GoalMilestone[];
  onClick: (day: Date, anchorEl: HTMLElement) => void;
}

const CustomDay: React.FC<CustomDayProps> = ({
  day,
  selectedDay,
  outsideCurrentMonth,
  events,
  milestones,
  onClick,
}) => {
  const theme = useTheme();
  const dateStr = format(day, 'yyyy-MM-dd');
  const event = events.find(e => e.date === dateStr);
  const milestone = milestones.find(m => m.date === dateStr);
  const isToday = isSameDay(day, new Date());
  const isSelected = selectedDay ? isSameDay(day, selectedDay) : false;
  const isPast = isBefore(day, new Date()) && !isToday;

  // Get styling based on event/milestone
  let bgColor = 'transparent';
  let borderColor = 'transparent';
  let emoji = '';
  let locationEmoji = '';

  if (milestone) {
    bgColor = alpha('#ffc107', 0.2);
    borderColor = '#ffc107';
    emoji = milestone.type === 'race' ? '🏆' : milestone.type === 'taper-start' ? '📉' : '📈';
  } else if (event) {
    const config = EVENT_TYPE_CONFIG[event.eventType];
    bgColor = alpha(config.color, 0.2);
    borderColor = config.color;
    emoji = config.emoji;
    if (event.location) {
      locationEmoji = LOCATION_CONFIG[event.location].emoji;
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick(day, e.currentTarget);
  };

  return (
    <Box
      component="button"
      onClick={handleClick}
      disabled={isPast && !event && !milestone}
      aria-label={`${format(day, 'EEEE, MMMM d')}${event ? `, ${EVENT_TYPE_CONFIG[event.eventType].label}` : ''}${milestone ? `, ${milestone.label}` : ''}`}
      sx={{
        width: 40,
        height: 40,
        p: 0,
        border: '2px solid',
        borderColor: isSelected ? theme.palette.primary.main : borderColor,
        borderRadius: '50%',
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.2) : bgColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isPast && !event && !milestone ? 'default' : 'pointer',
        opacity: outsideCurrentMonth ? 0.3 : isPast ? 0.5 : 1,
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover:not(:disabled)': {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          borderColor: theme.palette.primary.main,
        },
        '&:disabled': {
          opacity: 0.3,
          cursor: 'not-allowed',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: isToday ? 700 : 400,
          color: isToday ? theme.palette.primary.main : 'inherit',
          fontSize: '0.75rem',
          lineHeight: 1,
        }}
      >
        {format(day, 'd')}
      </Typography>
      {(emoji || locationEmoji) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            display: 'flex',
            fontSize: '0.6rem',
          }}
        >
          {emoji && <span>{emoji}</span>}
          {locationEmoji && <span>{locationEmoji}</span>}
        </Box>
      )}
    </Box>
  );
};

/**
 * Week view for mobile
 */
interface WeekViewProps {
  currentDate: Date;
  events: ScheduleEvent[];
  milestones: GoalMilestone[];
  selectedDay: Date | null;
  onDayClick: (day: Date, anchorEl: HTMLElement) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  milestones,
  selectedDay,
  onDayClick,
  onPreviousWeek,
  onNextWeek,
}) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  return (
    <Box>
      {/* Week navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={onPreviousWeek} aria-label="Previous week">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
        </Typography>
        <IconButton onClick={onNextWeek} aria-label="Next week">
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Week days */}
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between' }}>
        {weekDays.map(day => (
          <Box key={day.toISOString()} sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {format(day, 'EEE')}
            </Typography>
            <CustomDay
              day={day}
              selectedDay={selectedDay}
              outsideCurrentMonth={false}
              events={events}
              milestones={milestones}
              onClick={onDayClick}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Month view for desktop using custom grid
 */
interface MonthViewProps {
  currentDate: Date;
  events: ScheduleEvent[];
  milestones: GoalMilestone[];
  selectedDay: Date | null;
  onDayClick: (day: Date, anchorEl: HTMLElement) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  milestones,
  selectedDay,
  onDayClick,
  onPreviousMonth,
  onNextMonth,
}) => {
  // Generate calendar grid
  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Group days into weeks
  const weeks: Date[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Box>
      {/* Month navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={onPreviousMonth} aria-label="Previous month">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {format(currentDate, 'MMMM yyyy')}
        </Typography>
        <IconButton onClick={onNextMonth} aria-label="Next month">
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Day headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
        {dayNames.map(name => (
          <Typography
            key={name}
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', fontWeight: 500 }}
          >
            {name}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {weeks.map((week, weekIndex) => (
          <Box
            key={weekIndex}
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}
          >
            {week.map(day => (
              <Box key={day.toISOString()} sx={{ display: 'flex', justifyContent: 'center' }}>
                <CustomDay
                  day={day}
                  selectedDay={selectedDay}
                  outsideCurrentMonth={!isSameMonth(day, currentDate)}
                  events={events}
                  milestones={milestones}
                  onClick={onDayClick}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Schedule Agenda component
 * Displays a calendar for managing training schedule overrides
 * Shows month view on desktop, week view on mobile
 */
export const ScheduleAgenda: React.FC<ScheduleAgendaProps> = ({
  scheduleEvents,
  onEventsChange,
  trainingGoal,
  hasPendingChanges = false,
  onUpdateSchedule,
  isUpdating = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Generate goal milestones
  const goalMilestones = useMemo(() => generateGoalMilestones(trainingGoal), [trainingGoal]);

  // Find existing event for selected day
  const selectedEvent = useMemo(() => {
    if (!selectedDay) return undefined;
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    return scheduleEvents.find(e => e.date === dateStr);
  }, [selectedDay, scheduleEvents]);

  // Check if selected day is a goal milestone
  const selectedMilestone = useMemo(() => {
    if (!selectedDay) return undefined;
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    return goalMilestones.find(m => m.date === dateStr);
  }, [selectedDay, goalMilestones]);

  // Handle day click
  const handleDayClick = useCallback(
    (day: Date, element: HTMLElement) => {
      setSelectedDay(day);
      if (isMobile) {
        setIsModalOpen(true);
      } else {
        setAnchorEl(element);
      }
    },
    [isMobile]
  );

  // Handle popover close
  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedDay(null);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDay(null);
  }, []);

  // Handle save event
  const handleSaveEvent = useCallback(
    (eventData: {
      eventType: ScheduleEventType;
      location?: ScheduleEventLocation;
      note?: string;
    }) => {
      if (!selectedDay) return;

      const dateStr = format(selectedDay, 'yyyy-MM-dd');
      const now = new Date().toISOString();

      // Check if updating existing event or creating new one
      const existingIndex = scheduleEvents.findIndex(e => e.date === dateStr);

      let newEvents: ScheduleEvent[];
      if (existingIndex >= 0) {
        // Update existing
        newEvents = [...scheduleEvents];
        newEvents[existingIndex] = {
          ...newEvents[existingIndex],
          ...eventData,
          updatedAt: now,
        };
      } else {
        // Create new
        const newEvent: ScheduleEvent = {
          id: generateEventId(),
          date: dateStr,
          ...eventData,
          createdAt: now,
          updatedAt: now,
        };
        newEvents = [...scheduleEvents, newEvent];
      }

      // Clean up past events (auto-cleanup)
      const today = format(new Date(), 'yyyy-MM-dd');
      newEvents = newEvents.filter(e => e.date >= today);

      onEventsChange(newEvents);
    },
    [selectedDay, scheduleEvents, onEventsChange]
  );

  // Handle delete event
  const handleDeleteEvent = useCallback(() => {
    if (!selectedDay) return;

    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    const newEvents = scheduleEvents.filter(e => e.date !== dateStr);
    onEventsChange(newEvents);
  }, [selectedDay, scheduleEvents, onEventsChange]);

  // Navigation handlers
  const handlePreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handlePreviousWeek = () => setCurrentDate(addDays(currentDate, -7));
  const handleNextWeek = () => setCurrentDate(addDays(currentDate, 7));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          📅 Schedule Agenda
        </Typography>
        {hasPendingChanges && onUpdateSchedule && (
          <Button
            variant="contained"
            size="small"
            startIcon={isUpdating ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={onUpdateSchedule}
            disabled={isUpdating}
            aria-label="Update training schedule"
          >
            {isUpdating ? 'Updating...' : 'Update Schedule'}
          </Button>
        )}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {(Object.keys(EVENT_TYPE_CONFIG) as ScheduleEventType[]).map(type => {
          const config = EVENT_TYPE_CONFIG[type];
          return (
            <Box
              key={type}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: alpha(config.color, 0.1),
                border: `1px solid ${alpha(config.color, 0.3)}`,
              }}
            >
              <span style={{ fontSize: '0.75rem' }}>{config.emoji}</span>
              <Typography variant="caption">{config.label}</Typography>
            </Box>
          );
        })}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: alpha('#ffc107', 0.1),
            border: `1px solid ${alpha('#ffc107', 0.3)}`,
          }}
        >
          <span style={{ fontSize: '0.75rem' }}>🏆</span>
          <Typography variant="caption">Goal</Typography>
        </Box>
      </Box>

      {/* Calendar view */}
      {isMobile ? (
        <WeekView
          currentDate={currentDate}
          events={scheduleEvents}
          milestones={goalMilestones}
          selectedDay={selectedDay}
          onDayClick={handleDayClick}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
        />
      ) : (
        <MonthView
          currentDate={currentDate}
          events={scheduleEvents}
          milestones={goalMilestones}
          selectedDay={selectedDay}
          onDayClick={handleDayClick}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
        />
      )}

      {/* Popover editor (desktop) */}
      <EventEditorPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handlePopoverClose}
        selectedDate={selectedDay}
        existingEvent={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={selectedEvent ? handleDeleteEvent : undefined}
        isGoalDate={Boolean(selectedMilestone)}
        goalLabel={selectedMilestone?.label}
      />

      {/* Modal editor (mobile) */}
      <EventEditorModal
        open={isModalOpen}
        onClose={handleModalClose}
        selectedDate={selectedDay}
        existingEvent={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={selectedEvent ? handleDeleteEvent : undefined}
        isGoalDate={Boolean(selectedMilestone)}
        goalLabel={selectedMilestone?.label}
      />
    </Paper>
  );
};

export default ScheduleAgenda;
